<script setup>
// «Счета» on the phone home of "Capital" and of a composite portfolio (docs/design/SPEC.md, «Главная»):
// where the money is, largest first; a tap opens the account.
import { computed, ref } from 'vue';
import { t } from '../i18n';
import { accountTypeLabel, formatMoney, formatSignedPercent, signClass } from '../format';
import { portfolioTotals } from '../portfolioView';

const props = defineProps({
  // Account views (portfolioView.accountView)
  accounts: {
    type: Array,
    required: true,
  },
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  fxRates: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['select']);

const FIRST = 5;
const showAll = ref(false);

const rows = computed(() => props.accounts
  .map((account) => ({ account, totals: portfolioTotals(account, props.livePrices, props.fxRates) }))
  .sort((a, b) => (b.totals.total ?? 0) - (a.totals.total ?? 0)));

const visible = computed(() => (showAll.value ? rows.value : rows.value.slice(0, FIRST)));
</script>

<template>
  <section class="rounded-3xl bg-slate-900 py-2">
    <h2 class="flex items-baseline justify-between gap-3 px-5 pb-1 pt-3">
      <span class="text-[1.0625rem] font-semibold text-slate-100">{{ t('accounts.title') }}</span>
      <span class="text-[0.8125rem] tabular-nums text-slate-500">{{ rows.length }}</span>
    </h2>
    <ul>
      <li
        v-for="{ account, totals } in visible"
        :key="account.key"
      >
        <button
          type="button"
          class="flex min-h-14 w-full items-center gap-3 px-5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          @click="emit('select', account.key)"
        >
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ account.name || account.account_id }}</span>
            <span class="mt-0.5 block text-[0.8125rem] leading-4 text-slate-500">{{ accountTypeLabel(account.type) }}</span>
          </span>
          <span class="shrink-0 text-right tabular-nums">
            <span class="block text-[0.9375rem] leading-5 text-slate-100">{{ formatMoney(totals.total, account.currency) }}</span>
            <span
              v-if="totals.dailyPercent !== null"
              class="mt-0.5 block text-[0.8125rem] leading-4"
              :class="signClass(Math.round(totals.dailyPercent * 10) / 10)"
            >{{ formatSignedPercent(totals.dailyPercent) }}</span>
          </span>
          <svg
            class="size-4 shrink-0 text-slate-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </li>
    </ul>
    <div
      v-if="!showAll && rows.length > FIRST"
      class="px-5 pb-3 pt-1"
    >
      <button
        type="button"
        class="h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-700"
        @click="showAll = true"
      >
        {{ t('accounts.showAll', { count: rows.length }) }}
      </button>
    </div>
  </section>
</template>
